import { useLang } from "@/context/LanguageContext";
import { Reveal, ChapterLabel, MaskLines } from "@/components/Shared";
import { ArrowUpRight } from "lucide-react";

export const EventSummary = () => {
  const { t } = useLang();
  const E = t.events;
  return (
    <section data-testid="events-summary-section" className="relative overflow-hidden border-b border-white/5 px-6 py-24 sm:px-12 sm:py-32 lg:px-20">
      <span className="pointer-events-none absolute -right-6 top-8 select-none font-serif text-[22vw] leading-none text-outline opacity-30 lg:text-[14vw]">m4e</span>

      <div className="grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Reveal><ChapterLabel no="01" label={E.summaryLabel} /></Reveal>
          <MaskLines as="h2" text={E.summaryTitle} className="font-serif text-4xl font-light leading-[0.95] text-[#F9F8F6] sm:text-6xl lg:text-[4.2vw]" />
          <Reveal delay={0.3} className="mt-8">
            <a
              href="https://www.music4events.info/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="events-summary-link"
              className="group inline-flex items-center gap-3 font-sans text-[11px] font-medium uppercase tracking-[0.3em] text-[#C5A059]"
            >
              {E.summaryLink}
              <span className="h-px w-8 bg-[#C5A059] transition-all duration-500 group-hover:w-16" />
              <ArrowUpRight className="h-4 w-4" />
            </a>
          </Reveal>
        </div>
        <div className="space-y-6 lg:col-span-6 lg:col-start-7 lg:pt-4">
          {E.summary.map((p, i) => (
            <Reveal key={i} delay={0.1 + i * 0.1}>
              <p className={`font-sans text-sm leading-loose text-[#A1A1AA] sm:text-base ${i === 0 ? "drop-cap" : ""}`}>{p}</p>
            </Reveal>
          ))}
        </div>
      </div>

      <div className="mt-20 grid gap-px border border-white/10 bg-white/10 md:grid-cols-3">
        {E.formations.map((f, i) => (
          <Reveal key={f.name} delay={i * 0.08} className="bg-[#0B0B0C]">
            <div data-testid={`formation-${i}`} className="group flex h-full flex-col p-8 transition-colors duration-500 hover:bg-[#141416] sm:p-10">
              <span className="font-mono text-[10px] tracking-[0.35em] text-[#C5A059]">{String(i + 1).padStart(2, "0")} · {f.tag}</span>
              <h3 className="mt-5 font-serif text-3xl font-light text-[#F9F8F6] transition-colors duration-300 group-hover:text-[#C5A059] sm:text-4xl">{f.name}</h3>
              <p className="mt-5 flex-1 font-sans text-sm leading-relaxed text-[#A1A1AA]">{f.desc}</p>
              <p className="mt-8 border-t border-white/10 pt-5 font-mono text-[10px] leading-relaxed tracking-[0.15em] text-[#71717A]">{f.lineup}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <div className="mt-20 grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <Reveal><ChapterLabel label={E.clientsLabel} /></Reveal>
          <div className="mt-6 flex flex-wrap gap-2.5">
            {E.clients.map((c, i) => (
              <Reveal key={c} delay={Math.min(i * 0.02, 0.5)}>
                <span data-testid="events-client" className="inline-block border border-white/10 px-3.5 py-1.5 font-sans text-[11px] uppercase tracking-[0.15em] text-[#F9F8F6]/75 transition-colors duration-300 hover:border-[#C5A059]/60 hover:text-[#C5A059]">{c}</span>
              </Reveal>
            ))}
          </div>
        </div>
        <div className="space-y-10 lg:col-span-5 lg:col-start-8">
          <Reveal><ChapterLabel label={E.testimonialsLabel} /></Reveal>
          {E.testimonials.map((q, i) => (
            <Reveal key={i} delay={0.1 + i * 0.1}>
              <blockquote data-testid={`events-testimonial-${i}`} className="border-l border-[#C5A059]/40 pl-6">
                <p className="font-serif text-lg font-light italic leading-relaxed text-[#F9F8F6]/90 sm:text-xl">„{q.quote}“</p>
                <cite className="mt-3 block font-mono text-[10px] uppercase not-italic tracking-[0.3em] text-[#71717A]">— {q.author}</cite>
              </blockquote>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
